import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DownloadSimple, FilePdf, CheckCircle, HandCoins } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { PF, fmtEur, currentMonth } from './pfApi';
import { fmtMonth } from '@/utils/dateFmt';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const STATUS_LABEL = { por_pedir: 'Por pedir', pedido: 'Pedido', recebido_parcial: 'Recebido parcial', recebido: 'Recebido' };
const STATUS_CLS = { por_pedir: 'bg-brand-bg text-brand-muted', pedido: 'bg-amber-100 text-amber-700', recebido_parcial: 'bg-blue-100 text-blue-700', recebido: 'bg-brand-green/10 text-brand-green' };

export default function PfDuarte() {
  const [data, setData] = useState({ by_ambito: {}, saldo_aberto: 0 });
  const [loading, setLoading] = useState(true);
  const [receiveTarget, setReceiveTarget] = useState(null);
  const [receiveAmount, setReceiveAmount] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${PF}/duarte/imputar`); setData(data); } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const setStatus = async (id, status) => {
    try { await axios.put(`${PF}/duarte/${id}/status`, { status }); toast.success('Estado atualizado'); fetchData(); } catch { toast.error('Erro'); }
  };

  const openReceive = (m) => { setReceiveTarget(m); setReceiveAmount(String((m.amount_duarte - m.amount_received).toFixed(2))); };

  const confirmReceive = async () => {
    const amount = parseFloat(String(receiveAmount).replace(',', '.'));
    if (!amount || amount <= 0) { toast.error('Valor inválido'); return; }
    try { await axios.post(`${PF}/duarte/${receiveTarget.id}/receive`, { amount_received: amount }); toast.success('Reembolso registado'); setReceiveTarget(null); fetchData(); }
    catch { toast.error('Erro ao registar'); }
  };

  const exportText = () => window.open(`${PF}/duarte/export/text?month=${currentMonth()}`, '_blank');
  const exportPdf = () => window.open(`${PF}/duarte/export/pdf?month=${currentMonth()}`, '_blank');

  return (
    <div data-testid="pf-duarte-tab">
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5 flex items-center justify-between">
        <div>
          <p className="text-[11px] text-amber-700 mb-0.5 flex items-center gap-1"><HandCoins size={13} /> Saldo aberto (Duarte deve)</p>
          <p className="text-2xl font-heading font-semibold text-amber-700" data-testid="pf-duarte-saldo-aberto">{fmtEur(data.saldo_aberto)}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportText} className="p-2 rounded-full bg-white border border-amber-200 text-amber-700" title="Exportar texto" data-testid="pf-duarte-export-text"><DownloadSimple size={16} /></button>
          <button onClick={exportPdf} className="p-2 rounded-full bg-white border border-amber-200 text-amber-700" title="Exportar PDF" data-testid="pf-duarte-export-pdf"><FilePdf size={16} /></button>
        </div>
      </div>

      {loading && <p className="text-sm text-brand-muted text-center">A carregar...</p>}
      {!loading && Object.keys(data.by_ambito).length === 0 && <p className="text-sm text-brand-placeholder text-center">Sem despesas partilhadas registadas.</p>}

      {Object.entries(data.by_ambito).map(([ambito, ambitoData]) => (
        <div key={ambito} className="mb-6" data-testid={`pf-duarte-ambito-${ambito}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-brand-text bg-brand-green/10 px-2 py-1 rounded-full">{ambito}</span>
            <span className="text-[11px] text-brand-muted">saldo aberto {fmtEur(ambitoData.saldo_aberto)}</span>
          </div>
          {Object.entries(ambitoData.by_month).map(([month, items]) => (
            <div key={month} className="mb-4">
              <p className="text-xs font-medium text-brand-muted mb-2">{/^\d{4}-\d{2}$/.test(month) ? fmtMonth(month) : month}</p>
              <div className="space-y-1.5">
                {items.map(m => (
                  <div key={m.id} className="bg-white border border-brand-border rounded-xl p-3 flex items-center justify-between gap-2" data-testid={`pf-duarte-item-${m.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-brand-text truncate">{m.note || '(sem descrição)'}</p>
                      <p className="text-[11px] text-brand-muted">{fmtDatePt(m.date)} · total {fmtEur(m.amount)} · parte Duarte <strong>{fmtEur(m.amount_duarte)}</strong></p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-[10px] font-medium px-2 py-1 rounded-full ${STATUS_CLS[m.reimbursement_status]}`} data-testid={`pf-duarte-status-${m.id}`}>{STATUS_LABEL[m.reimbursement_status]}</span>
                      {m.reimbursement_status === 'por_pedir' && <button onClick={() => setStatus(m.id, 'pedido')} className="text-[11px] text-brand-green hover:underline" data-testid={`pf-duarte-mark-pedido-${m.id}`}>Marcar pedido</button>}
                      {m.reimbursement_status !== 'recebido' && (
                        <button onClick={() => openReceive(m)} className="text-brand-green p-1" title="Registar recebimento" data-testid={`pf-duarte-receive-${m.id}`}><CheckCircle size={16} /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}

      <Dialog open={!!receiveTarget} onOpenChange={(o) => !o && setReceiveTarget(null)}>
        <DialogContent data-testid="pf-duarte-receive-dialog">
          <DialogHeader><DialogTitle>Registar reembolso do Duarte</DialogTitle></DialogHeader>
          <p className="text-sm text-brand-muted mb-2">{receiveTarget?.note}</p>
          <Input inputMode="decimal" value={receiveAmount} onChange={e => setReceiveAmount(e.target.value)} className="rounded-xl border-brand-border mb-3" data-testid="pf-duarte-receive-amount" />
          <button onClick={confirmReceive} className="w-full bg-brand-green text-white rounded-full py-2.5 text-sm font-medium" data-testid="pf-duarte-receive-confirm">Confirmar</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
